import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import { CardLayout, DeleteModal, MTable } from "../v3";
import { ProjectContractTableColumns } from "../../pages/project/constants";
import { AnalyticsService, FinanceService, PurchaseService } from "../../services";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";
import { DELETE_SUBCONTRACTOR_TRANSACTION, EDIT_SUBCONTRACTOR_TRANSACTION } from "src/constants";

export const ProjectContracts = () => {
  const [contracts, setContracts] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "project-contracts",
    defaults: { sortModel: [{ field: "date", sort: "desc" }] },
  });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, recordId: null });

  const canEditContract = useHasPermission(EDIT_SUBCONTRACTOR_TRANSACTION);
  const canDeleteContract = useHasPermission(DELETE_SUBCONTRACTOR_TRANSACTION);

  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const fetchData = useCallback(
    async (transactionFilters = {}) => {
      setLoading(true);

      try {
        const { data: contractAnalytics } = await AnalyticsService.getSubcontractorAnalytics({
          ...transactionFilters,
          type: "expense",
          projectId: id,
        });
        const subcontractorIds = contractAnalytics?.rows?.map((item) => item?.subcontractorId);
        if (subcontractorIds?.length > 0) {
          const { data: subcontractors } = await PurchaseService.getVendors({
            ids: subcontractorIds.join(","),
            limit: subcontractorIds.length,
            type: "SUBCONTRACTOR",
            includeDeleted: true,
          });
          setSubcontractors(subcontractors.rows);
        }
        setContracts(contractAnalytics.rows);
        setTotalCount(contractAnalytics.count);
      } catch (error) {
        toast.error("Failed to fetch contracts");
        console.error("Failed to fetch contracts");
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    if (!dateRange) return; // wait for DateRangeInput default/apply callback

    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
    });
  }, [id, sortModel, paginationState, fetchData, dateRange]);

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    // WHY: Apply picked date window directly in analytics filters and reset page safely.
    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(ProjectContractTableColumns());
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Contracts" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Contracts",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = (params, event) => navigate(`/subcontractor/${params.row.subcontractorId}/payment/${params.row.id}`);

  const openDeleteModal = (contract) => {
    setDeleteModal({ isOpen: true, recordId: contract.id });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, recordId: null });
  };

  const handleDelete = async () => {
    try {
      await FinanceService.deleteExpense(deleteModal.recordId);
      toast.success("Contract deleted successfully");
      fetchData({
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
        fromDate: dateRange?.startDate,
        toDate: dateRange?.endDate,
      });
    } catch (error) {
      toast.error("Failed to delete contract");
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = (contractData, actionType) => {
    switch (actionType) {
      case "edit":
        navigate(`/transaction/edit/${contractData.id}?subcontractorId=${contractData.subcontractorId}`);
        break;
      case "delete":
        openDeleteModal(contractData);
        break;
      default:
        return null;
    }
  };
  const columnHeaders = ProjectContractTableColumns(handleActions, canEditContract, canDeleteContract);

  const rows = contracts?.map((contract, index) => {
    const subcontractor = subcontractors.find((subcontractor) => subcontractor.id === contract.subcontractorId);
    return {
      id: contract.id,
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      date: moment(contract.date).format("DD MMM YYYY"),
      subcontractor: subcontractor?.name,
      contract: contract?.title || "-",
      amount: Number(contract.amount),
      type: contract.type,
      subcontractorId: contract?.subcontractorId,
    };
  });

  const DEFAULT_SORT = [{ field: "date", sort: "desc" }];

  return (
    <CardLayout>
      <MTable
        // Date range data
        showDateRangePicker
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        // Export data
        showExportButton
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={columnHeaders}
        tableRows={rows}
        rowCount={totalCount}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        defaultSortModel={DEFAULT_SORT}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this contract transaction?"
      />
    </CardLayout>
  );
};
